import { useEffect, useState } from 'react';

import { esPut, esRead } from '../../common/EsFetch';
import { EsCheckbox } from '../../common/EsPrime';
import { useEsStrings } from '../../common/EsStrings';
import { API_VOICE_PTT, esPttKeyLabel, PTT_DEFAULT_KEY } from '../../common/EsVoice';

/*
 * The account page's push-to-talk (es-voice.md §9, phase 3): voice activation by default, or
 * talk only while a key is held. The key is chosen by pressing it on the button, and stored as
 * its KeyboardEvent.code, so it is the same key on any keyboard layout. Saved at once, as
 * "I can translate" is. Drawn while rolloutEsVoice is on.
 */
export default function EsAccountVoice() {
    const t = useEsStrings();
    const [ ptt, setPtt ] = useState( null );
    const [ choosing, setChoosing ] = useState( false );
    const [ busy, setBusy ] = useState( false );
    const [ error, setError ] = useState( null );

    useEffect( () => {
        esRead( API_VOICE_PTT ).then( setPtt ).catch( () => setPtt( { ptt: false, key: PTT_DEFAULT_KEY } ) );
    }, [] );

    const save = async ( pNext ) => {
        setBusy( true );
        setError( null );
        try {
            setPtt( await esPut( API_VOICE_PTT, pNext ) ?? pNext );
        }
        catch ( aError ) {
            setError( aError?.message ?? t( 'account-settings-voice-not-saved', 'That did not save.' ) );
        }
        finally {
            setBusy( false );
        }
    };

    // While choosing, the next key down on the button is the talk key. Escape keeps the old
    // one, and Tab is never taken: it is how a keyboard leaves the button.
    const pick = ( pEvent ) => {
        if ( pEvent.code === 'Tab' ) {
            setChoosing( false );
            return;
        }
        pEvent.preventDefault();
        setChoosing( false );
        if ( pEvent.code === 'Escape' || !pEvent.code ) return;
        save( { ptt: true, key: pEvent.code } );
    };

    if ( !ptt ) return null;

    const key = esPttKeyLabel( ptt.key );
    return (
        <div data-testid="es-voice-ptt-setting">
            <div className="flex items-center gap-3">
                <EsCheckbox id="esVoicePtt" name="voicePtt" checked={ ptt.ptt } disabled={ busy }
                            data-testid="es-voice-ptt"
                            onChange={ ( pEvent ) => save( { ptt: !!pEvent.target.value, key: ptt.key } ) } />
                <label htmlFor="esVoicePtt" className="text-sm text-body">
                    { t( 'account-settings-voice-ptt', 'Push to talk: speak only while a key is held' ) }
                </label>
            </div>
            { ptt.ptt &&
                <div className="mt-2 flex items-center gap-3 text-sm">
                    <span className="text-body">{ t( 'account-settings-voice-key', 'Talk key' ) }</span>
                    <button type="button" data-testid="es-voice-ptt-key" disabled={ busy }
                            aria-pressed={ choosing }
                            aria-label={ choosing ? t( 'account-settings-voice-key-press', 'Press the key to talk with' )
                                : t( 'account-settings-voice-key-choose', 'Talk key {key}; choose another', { key } ) }
                            onClick={ () => setChoosing( true ) }
                            onKeyDown={ choosing ? pick : undefined }
                            onBlur={ () => setChoosing( false ) }
                            className="rounded-md border border-field-edge bg-field px-3 py-1 font-mono text-body ring-focus focus:ring-2 disabled:opacity-50">
                        { choosing ? t( 'account-settings-voice-key-press', 'Press the key to talk with' ) : key }
                    </button>
                </div> }
            <p className="mt-2 text-xs text-aside">
                { t( 'account-settings-voice-hint', 'The key does nothing while you are typing in a box. Voice activation is the default.' ) }
            </p>
            { error && <p data-testid="es-voice-ptt-error" role="alert" className="mt-2 text-xs text-danger">{ error }</p> }
        </div>
    );
}
